import React, { SyntheticEvent } from 'react';
import styled from 'styled-components';
import IAddress from '@sunbit-dev/common/models/IAddress';
import ApplicationStatus from '@sunbit-dev/common/enums/cardApply/ApplicationStatus';
import State, { StateCodes } from '@sunbit-dev/common/enums/State';
import { isAddress } from '@sunbit-dev/common/helpers/address';
import { MixpanelCardApplylEvent } from '@sunbit-dev/common/enums/cardApply/MixpanelCardApplylEvent';
import Popup from '../../../ui/popup/Popup';
import CommonSection from '../commonSection/CommonSection';
import SectionTitle from '../sectionTitle/SectionTitle';
import CardApplyLink from '../cardApplyLinkButton/CardApplyLinkButton';
import { ButtonColor, SButton } from '../../../ui/button/Button';
import InputGroup from '../../../ui/inputGroup/InputGroup';
import InputWithRifm from '../../../ui/inputFormWithRifm';
import zipCodeRecipe from '../../../ui/rifmRecipes/zipCode';
import Input2 from '../../../ui/input/Input2';
import { Dropdown } from '../../../ui/dropdown/Dropdown';
import CardApplyButton from '../cardApplyButton/CardApplyButton';
import AddressAutocomplete from '../../../ui/AddressAutocomplete/AddressAutocomplete';
import { PrivacyOptions } from '@sunbit-dev/common/enums/Datadog';

const statesOptions = Object.keys(State).map((state: StateCodes) => ({ value: state as string, label: State[state] as string }));

interface IApplicationDetails {
  id?: number;
  customerName?: {
    firstName: string;
    middleName: string;
    lastName: string;
  };
  customerPhone?: string;
  customerEmail?: string;
  applicationStatus?: ApplicationStatus;
  accountCreationDate?: string;
  militaryLendingAct?: boolean;
  plasticAddress?: IAddress;
}

// ############################ Styles ###########################
// tslint:disable:variable-name

const SPlasticAddress = styled.section``;

const SPlasticAddressInner = styled(Popup)(
  ({ theme }) => `
  margin: 0;
  overflow-y: scroll;
  max-height: 100%;
    /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
  display: none;
}
  -ms-overflow-style: none;
  scrollbar-width: none;
  @media only screen and ${theme.device.laptopS}{
    overflow-y: visible;

  }
  `
);

const STitle = styled.h1(
  ({ theme }) => `
  font-weight: ${theme.font.weight.normal};
  color: ${theme.colors.dark};
  font-size: ${theme.font.size.medium3};
  @media only screen and ${theme.device.laptopS} {
    font-size: ${theme.font.size.mediumLarge};
  }
`
);

const SInput = styled(Input2)(
  ({ theme }) => `
  @media only screen and ${theme.device.laptopS}{
    margin-bottom: ${theme.spacing.large}px;
    margin-right: 3%;
  }
  `
);

const SSectionTitle = styled(SectionTitle as any)(
  ({ theme }) => `
  @media only screen and ${theme.device.laptopS} {
    font-size: ${theme.font.size.smallMedium2};
  }
`
);

const SCommonSectionWithMoreSpacing = styled(CommonSection as any)(
  ({ theme }) => `
  margin-bottom: ${theme.spacing.larger}px;
`
);

const SCommonSectionActions = styled(CommonSection as any)(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  ${SButton}:not(:first-child) {
    margin-left: ${theme.spacing.large}px;
  }
`
);

const SAdressReadSection = styled.section(
  ({ theme }) => `
  padding: ${theme.spacing.large}px ${theme.spacing.medium}px;
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  flex-direction: column;
  @media only screen and ${theme.device.laptopS} {
    flex-direction: row;
  }
`
);

const SStateDropdown = styled.div(
  ({ theme }) => `
  @media only screen and ${theme.device.laptopS} {
    display: flex;
    width:190px;
    margin-right:20px;
  }
`
);

const SAdressRead = styled.section(
  ({ theme }) => `
  color: ${theme.colors.primary};
  font-weight: ${theme.font.weight.bold};
  font-size: ${theme.font.size.smallMedium};
  margin-bottom: ${theme.spacing.medium}px;
  @media only screen and ${theme.device.laptopS} {
    margin-bottom: 0;
    font-size: ${theme.font.size.medium};
  }
`
);

const SInlineText = styled(SAdressRead as any)(() => 'display: inline;');

// tslint:enable:variable-name
// ############################ End Styles ###########################

// tslint:disable-next-line:no-empty-interface
export interface IPlasticAddressProps {
  isReferral: boolean;
  applicationDetails: IApplicationDetails;
  submit: (address: IAddress) => void;
  onClickChangeAddress: (onClick: Function, event: MixpanelCardApplylEvent) => (data?: IAddress | SyntheticEvent) => void;
  isAddressAutocomplete?: boolean;
}

// tslint:disable-next-line:variable-name
const PlasticAddress: React.FC<IPlasticAddressProps> = (props) => {
  const [changeAddressMode, setChangeAddressMode] = React.useState<boolean>(false);

  const [address, setAddress] = React.useState<Partial<IAddress>>({
    address: '',
    city: '',
    state: StateCodes.CA,
    zipcode: '',
  });

  const [editAddress, setEditAddress] = React.useState<Partial<IAddress>>({
    address: '',
    city: '',
    state: undefined,
    zipcode: '',
  });

  const regex5ZipcodeDigits = new RegExp('^.*-$');
  const regexPreventLastCharNonDigitBug = new RegExp('^.*[a-z]$');

  React.useEffect(() => {
    setAddress({ ...props.applicationDetails.plasticAddress });
  }, [props.applicationDetails.plasticAddress]);

  // This is for a case where props zipcode is 9 characters and needs formatting before first load
  React.useEffect(() => {
    if (props.applicationDetails.plasticAddress) {
      if (props.applicationDetails.plasticAddress.zipcode.length > 5) {
        let formattedZipCode;
        if (regex5ZipcodeDigits.test(props.applicationDetails.plasticAddress.zipcode)) {
          formattedZipCode = props.applicationDetails.plasticAddress.zipcode.replace('-', '');
        }
        formattedZipCode = `${props.applicationDetails.plasticAddress.zipcode.substring(0, 5)}-${props.applicationDetails.plasticAddress.zipcode.substring(5)}`;
        setAddress({
          address: props.applicationDetails.plasticAddress.address,
          city: props.applicationDetails.plasticAddress.city,
          state: props.applicationDetails.plasticAddress.state,
          zipcode: formattedZipCode,
        });
      }
    }
  }, []);

  const handleAutocompleteSelection = (addressData: IAddress) => {
    setEditAddress((prev) => ({
      address: addressData.address || addressData.city || prev.address,
      city: addressData.city || '',
      zipcode: addressData.zipcode || prev.zipcode,
      state: addressData.state || prev.state,
    }));
  };
  const handleOnChange = React.useCallback(
    (value, name) => {
      setEditAddress({ ...editAddress, [name]: value });
    },
    [editAddress]
  );

  const handleZipcodeOnChangeEvent = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      // Fix zipcode Rifm 5 digits error
      if (regex5ZipcodeDigits.test(value)) {
        const fixedValue = value.replace('-', '');
        setEditAddress({ ...editAddress, [name]: fixedValue });
      } else if (regexPreventLastCharNonDigitBug.test(value)) {
        const fixedValue = value.slice(0, -1);
        setEditAddress({ ...editAddress, [name]: fixedValue });
      } else {
        setEditAddress({ ...editAddress, [name]: value });
      }
    },
    [handleOnChange]
  );

  const handleOnChangeEvent = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      handleOnChange(value, name);
    },
    [handleOnChange]
  );

  const toggleChangeAddressMode = React.useCallback(() => {
    if (!changeAddressMode) {
      setEditAddress({ ...address });
    }
    setChangeAddressMode(!changeAddressMode);
  }, [changeAddressMode, address]);
  const handleOnUpdateAddressClick = React.useCallback(() => {
    setAddress({ ...editAddress });
    toggleChangeAddressMode();
  }, [editAddress]);

  const handleOnCancelChangedAddressClick = React.useCallback(() => {
    toggleChangeAddressMode();
  }, [editAddress]);

  const noPOBOXAddressRegex = new RegExp(
    '^(?!\\s*(?:' +
      'P\\.?\\s*O\\.?\\s*(?:BOX|B\\.?O\\.?X)\\b|' +
      'P\\.?\\s*O\\.?\\s*B\\.?\\s*(?:#?\\d+)?\\b|' +
      'POST\\s+OFFICE\\s+BOX\\b|' +
      'POSTAL\\s+BOX\\b|' +
      'PO-BOX\\b|' +
      'P\\s+BOX\\b' +
    ')(?:\\s*#?[.\\s\\w\\d-]*)?).+$',
    'i'
  );
  const isValidNoPOBOXAddressDetails = (str: string): boolean => {
    // Block specific PO Box variations including "Post office box", "postal box", "PO-BOX", and "P BOX"
    return noPOBOXAddressRegex.test(str);
  };

  const isAddressCanUpdated = (editAddress: Partial<IAddress>) => {
    return isAddress(editAddress) && isValidNoPOBOXAddressDetails(editAddress.address);
  };

  const getEmptyFieldErrorMsg = (errorMsg: string, fieldValue?: string) => {
    return fieldValue ? undefined : errorMsg;
  };
  const getAddressFieldErrorIfNeeded = (fieldValue?: string) => {
    const emptyFieldMsg = getEmptyFieldErrorMsg('Please enter a valid address', fieldValue);
    return emptyFieldMsg ?? (isValidNoPOBOXAddressDetails(fieldValue!) ? undefined : 'Please enter a physical address. P.O. Boxes are not allowed.');
  };
  const handleConfirmAdressButtonClick = React.useCallback(() => {
    if (isAddress(address)) {
      props.submit(address);
    }
  }, [address]);

  const renderUpperText = () => {
    if (props.isReferral) {
      return (
        <SSectionTitle data-at="confirmAddressSubTitle">
          Before we proceed with your card application, please confirm your personal information. To update this information, please contact us at 855 678 6248.
        </SSectionTitle>
      );
    }
    return <SSectionTitle data-at="confirmAddressSubTitle">To proceed with your card application, please confirm or update your address.</SSectionTitle>;
  };

  const customerPhoneNumber = props.applicationDetails.customerPhone || '';
  const phoneNumberForDisplay = `(${customerPhoneNumber.substring(0, 3)}) ${customerPhoneNumber.substring(3, 6)}-${customerPhoneNumber.substring(6, 10)}`;

  const { city, state, zipcode } = address;
  return (
    <SPlasticAddressInner>
      <SPlasticAddress data-dd-privacy={PrivacyOptions.MaskUserInput}>
        <CommonSection>
          <STitle data-at="confirmAddressTitle">Please confirm your address.</STitle>
        </CommonSection>
        {!changeAddressMode && (
          <>
            <CommonSection>{renderUpperText()}</CommonSection>
            {props.applicationDetails.customerName && props.isReferral && (
              <SCommonSectionWithMoreSpacing data-dd-privacy={PrivacyOptions.Mask}>
                <SAdressRead style={{ marginBottom: '10px' }}>
                  {props.applicationDetails.customerName.firstName} {props.applicationDetails.customerName.middleName} {props.applicationDetails.customerName.lastName}
                </SAdressRead>
                <SInlineText>{props.applicationDetails.customerEmail}</SInlineText>
                <SInlineText style={{ marginLeft: '120px' }}>{phoneNumberForDisplay}</SInlineText>
              </SCommonSectionWithMoreSpacing>
            )}
            <SCommonSectionWithMoreSpacing>
              <SAdressReadSection>
                <SAdressRead data-dd-privacy={PrivacyOptions.Mask} data-at="selectedAdressText">
                  {address.address}
                  <br />
                  {`${city}, ${state} ${zipcode}`}
                </SAdressRead>
                <CardApplyLink dataAt="changeAddressButton" onClick={props.onClickChangeAddress(toggleChangeAddressMode, MixpanelCardApplylEvent.ChangeAddressPop_Views)}>
                  Change Address
                </CardApplyLink>
              </SAdressReadSection>
            </SCommonSectionWithMoreSpacing>
            <SCommonSectionActions>
              <CardApplyButton dataAt="confirmAddressButton" color={ButtonColor.dark} onClick={handleConfirmAdressButtonClick}>
                Continue with this address
              </CardApplyButton>
            </SCommonSectionActions>
          </>
        )}
        {changeAddressMode && (
          <>
            <CommonSection>
              <SSectionTitle data-at="updateAddressTitle">
                Please enter your current address below.
                <br /> Your Sunbit Card will also be sent to this address (if you are approved and complete your application).
              </SSectionTitle>
            </CommonSection>
            <SCommonSectionWithMoreSpacing>
              <InputGroup dataAt="streetAddressGroup">
                {props.isAddressAutocomplete ? (
                  <AddressAutocomplete
                    onSelect={props.onClickChangeAddress(handleAutocompleteSelection, MixpanelCardApplylEvent.ChangeAddressPop_UseAutocomplete)}
                    error={getAddressFieldErrorIfNeeded(editAddress.address)}
                    value={editAddress.address}
                    name="address"
                    label="Street Address (including unit #)"
                    onChange={handleOnChange}
                  />
                ) : (
                  <Input2 color={'primary'} dataAt="address" name="address" label="Street Address (including unit #)" value={editAddress.address} onChange={handleOnChangeEvent} />
                )}
              </InputGroup>
              <InputGroup dataAt="streetAddressGroup">
                <SInput
                  color={'primary'}
                  maxLength={25}
                  dataAt="city"
                  name="city"
                  label="City"
                  error={getEmptyFieldErrorMsg('Please enter your City', editAddress.city)}
                  value={editAddress.city}
                  onChange={handleOnChangeEvent}
                />
                <SStateDropdown data-dd-privacy={PrivacyOptions.Mask}>
                  <Dropdown
                    inputColor={'primary'}
                    className="inputInGroup"
                    dataAt="state"
                    name="state"
                    label="State"
                    dropdownOptions={statesOptions}
                    value={editAddress.state}
                    onChange={handleOnChange}
                  />
                </SStateDropdown>
                <InputWithRifm
                  error={getEmptyFieldErrorMsg('Please enter your Zip code', editAddress.zipcode)}
                  textColor={'primary'}
                  maxLength={10}
                  className="inputInGroup"
                  dataAt="zipcode"
                  name="zipcode"
                  label="Zipcode"
                  value={editAddress.zipcode}
                  onChange={handleZipcodeOnChangeEvent}
                  rifm={zipCodeRecipe}
                />
              </InputGroup>
            </SCommonSectionWithMoreSpacing>
            <SCommonSectionActions>
              <CardApplyButton dataAt="cancelChangeAddressButton" color={ButtonColor.neutral} onClick={handleOnCancelChangedAddressClick}>
                Cancel
              </CardApplyButton>
              <CardApplyButton dataAt="updateAddressButton" color={ButtonColor.primaryLightBlue} onClick={handleOnUpdateAddressClick} disabled={!isAddressCanUpdated(editAddress)}>
                Update Address
              </CardApplyButton>
            </SCommonSectionActions>
          </>
        )}
      </SPlasticAddress>
    </SPlasticAddressInner>
  );
};

export default PlasticAddress;
